import {
  CharitableOrganizationSearchClause,
  CharitableOrganizationSearchRequest,
} from "@dreambigger/shared/src/types/charitable-organization";

/**
 * Default results per request (overridable via the step's resultsPerPage
 * asset). The list is an infinite scroll, so this is invisible to the donor
 * — it's a tuning knob for request count vs. payload size. 24 fills any
 * screen in one round trip (8 couldn't, forcing an immediate second fetch
 * on tall screens) and cuts deep-scroll request volume 3×.
 */
export const RESULTS_PER_PAGE = 24;

// The API validator rejects limits outside 1–50.
const MIN_LIMIT = 1;
const MAX_LIMIT = 50;

/**
 * Results-per-request from step config: clamped to the API's valid range,
 * falling back to the default when absent or not a number.
 */
export const resolveResultsPerPage = (configured?: number): number => {
  if (typeof configured !== "number" || !Number.isFinite(configured)) {
    return RESULTS_PER_PAGE;
  }
  return Math.min(MAX_LIMIT, Math.max(MIN_LIMIT, Math.floor(configured)));
};

/**
 * The multi-match field list the free-text search box queries — one MUST
 * clause across all of these, mirroring the provider's own site search.
 */
export const MULTI_MATCH_FIELDS =
  "name,typeDescription,addresses.state,addresses.county,addresses.city,addresses.zip5,focuses.categoryDescription,purpose,webUrl";

/**
 * Cause options for the search's Cause filter.
 *
 * Label/code pairs sourced from the org-search provider's own frontend
 * constants (CAUSES_OPTIONS). Codes map to `focuses.categoryCode` search
 * clauses — EXCEPT "Church" (CONG), which is an organization *type* and maps
 * to a `typeCode` clause instead (see
 * composeCharitableOrganizationSearchRequest).
 */
export const causeOptions: { label: string; value: string }[] = [
  { label: "Aid the sick", value: "AS" },
  { label: "Animals", value: "ANML" },
  { label: "Arts/culture", value: "AC" },
  { label: "Camps/outdoor retreats", value: "CAMP" },
  { label: "Campus ministry", value: "CM" },
  { label: "Church", value: "CONG" },
  { label: "Clean water", value: "PCW" },
  { label: "Clothing", value: "CLOTH" },
  { label: "Disaster relief", value: "DR" },
  { label: "Education", value: "ED" },
  { label: "Environment", value: "ENV" },
  { label: "Families", value: "FAM" },
  { label: "Financial literacy", value: "FL" },
  { label: "Health", value: "HW" },
  { label: "Hunger", value: "HGR" },
  { label: "Inmate support", value: "VISIT" },
  { label: "Other", value: "OTHER" },
  { label: "Religious", value: "R" },
  { label: "Senior care", value: "EC" },
  { label: "Shelter", value: "HLESS" },
  { label: "Veterans", value: "P" },
  { label: "Youth", value: "CY" },
];

/**
 * The one cause option that is an organization type rather than a focus
 * category — searched via `typeCode`, not `focuses.categoryCode`.
 */
export const CONGREGATION_TYPE_CODE = "CONG";

/**
 * Cause options from step config (the vendor owns the cause taxonomy, so
 * config can add categories without a deploy): entries must have non-empty
 * string label/value; falls back to the built-in list when absent, invalid,
 * or empty after validation.
 */
export const resolveCauseOptions = (
  configured?: { label: string; value: string }[]
): { label: string; value: string }[] => {
  if (!Array.isArray(configured)) {
    return causeOptions;
  }
  const valid = configured.filter(
    (option) =>
      !!option &&
      typeof option.label === "string" &&
      option.label.trim() !== "" &&
      typeof option.value === "string" &&
      option.value.trim() !== ""
  );
  return valid.length > 0 ? valid : causeOptions;
};

/**
 * US state (and territory) options for the search's State filter — full
 * names as labels, 2-letter uppercase USPS codes as values (the org-search
 * API matches `addresses.state` on codes, e.g. "FL"). The 50 states + DC
 * use the same codes as the platform's allowedHomeStates convention;
 * territories are additionally included since organizations can be located
 * there.
 *
 * NOTE: distinct from `defaults.ts` `defaultStates`, whose values are full
 * names (used by address forms that store the full name).
 */
export const stateCodeOptions: { label: string; value: string }[] = [
  { label: "Alabama", value: "AL" },
  { label: "Alaska", value: "AK" },
  { label: "American Samoa", value: "AS" },
  { label: "Arizona", value: "AZ" },
  { label: "Arkansas", value: "AR" },
  { label: "California", value: "CA" },
  { label: "Colorado", value: "CO" },
  { label: "Connecticut", value: "CT" },
  { label: "Delaware", value: "DE" },
  { label: "District of Columbia", value: "DC" },
  { label: "Florida", value: "FL" },
  { label: "Georgia", value: "GA" },
  { label: "Guam", value: "GU" },
  { label: "Hawaii", value: "HI" },
  { label: "Idaho", value: "ID" },
  { label: "Illinois", value: "IL" },
  { label: "Indiana", value: "IN" },
  { label: "Iowa", value: "IA" },
  { label: "Kansas", value: "KS" },
  { label: "Kentucky", value: "KY" },
  { label: "Louisiana", value: "LA" },
  { label: "Maine", value: "ME" },
  { label: "Maryland", value: "MD" },
  { label: "Massachusetts", value: "MA" },
  { label: "Michigan", value: "MI" },
  { label: "Minnesota", value: "MN" },
  { label: "Mississippi", value: "MS" },
  { label: "Missouri", value: "MO" },
  { label: "Montana", value: "MT" },
  { label: "Nebraska", value: "NE" },
  { label: "Nevada", value: "NV" },
  { label: "New Hampshire", value: "NH" },
  { label: "New Jersey", value: "NJ" },
  { label: "New Mexico", value: "NM" },
  { label: "New York", value: "NY" },
  { label: "North Carolina", value: "NC" },
  { label: "North Dakota", value: "ND" },
  { label: "Northern Mariana Islands", value: "MP" },
  { label: "Ohio", value: "OH" },
  { label: "Oklahoma", value: "OK" },
  { label: "Oregon", value: "OR" },
  { label: "Pennsylvania", value: "PA" },
  { label: "Puerto Rico", value: "PR" },
  { label: "Rhode Island", value: "RI" },
  { label: "South Carolina", value: "SC" },
  { label: "South Dakota", value: "SD" },
  { label: "Tennessee", value: "TN" },
  { label: "Texas", value: "TX" },
  { label: "U.S. Virgin Islands", value: "VI" },
  { label: "Utah", value: "UT" },
  { label: "Vermont", value: "VT" },
  { label: "Virginia", value: "VA" },
  { label: "Washington", value: "WA" },
  { label: "West Virginia", value: "WV" },
  { label: "Wisconsin", value: "WI" },
  { label: "Wyoming", value: "WY" },
];

export interface CharitableOrganizationSearchCriteria {
  /** Free-text query (org name or keywords). */
  query?: string;
  /** Cause code from causeOptions (focuses.categoryCode, or CONG → typeCode). */
  cause?: string;
  city?: string;
  /** 2-letter uppercase state code. */
  state?: string;
  /** Zero-based page index. */
  page?: number;
  /** Results per request; defaults to RESULTS_PER_PAGE. */
  limit?: number;
}

/**
 * Composes the search request body from the UI criteria, mirroring how the
 * provider's own site builds its query:
 * - free text → ONE multi-match MUST clause across MULTI_MATCH_FIELDS
 * - cause → focuses.categoryCode MUST (except CONG, an org *type* →
 *   typeCode MUST)
 * - city → addresses.city MUST; state → addresses.state MUST
 * - NO sort — omitting it returns relevance-ranked results (best match
 *   first), matching the provider's site.
 */
export const composeCharitableOrganizationSearchRequest = ({
  query,
  cause,
  city,
  state,
  page = 0,
  limit = RESULTS_PER_PAGE,
}: CharitableOrganizationSearchCriteria): CharitableOrganizationSearchRequest => {
  const clauses: CharitableOrganizationSearchClause[] = [];

  const trimmedQuery = query?.trim();
  if (trimmedQuery) {
    clauses.push({
      clause: "MUST",
      searchText: trimmedQuery,
      fieldNames: MULTI_MATCH_FIELDS,
    });
  }

  if (cause) {
    clauses.push({
      clause: "MUST",
      searchText: cause,
      fieldNames:
        cause === CONGREGATION_TYPE_CODE ? "typeCode" : "focuses.categoryCode",
    });
  }

  const trimmedCity = city?.trim();
  if (trimmedCity) {
    clauses.push({
      clause: "MUST",
      searchText: trimmedCity,
      fieldNames: "addresses.city",
    });
  }

  if (state) {
    clauses.push({
      clause: "MUST",
      searchText: state,
      fieldNames: "addresses.state",
    });
  }

  return {
    clauses,
    page,
    limit,
  };
};

/**
 * True when the criteria are sufficient to run a search: at least one
 * criterion, and if free text is the ONLY criterion it must be at least 2
 * characters (prevents junk single-character relevance queries).
 */
export const hasSearchableCriteria = ({
  query,
  cause,
  city,
  state,
}: CharitableOrganizationSearchCriteria): boolean => {
  const trimmedQuery = query?.trim() ?? "";
  const hasFilter = Boolean(cause || city?.trim() || state);

  if (hasFilter) {
    return true;
  }

  return trimmedQuery.length >= 2;
};
